/* eslint-disable react/prop-types */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef
} from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Button, Col, Row } from 'react-bootstrap';
import DynamicNormalForm from './dynamicNormalForm';
import { DynamicTableForm } from './dynamicTableForm';
import { generateValidationSchema } from './validationSchema';
import { yupResolver } from '@hookform/resolvers/yup';
import { parse, format, isValid } from 'date-fns';
import { ModalForm } from './dynamicModalForm';
import { useDependencyManager } from './useDependencyManager';
import apiClient from 'api/client';

const DynamicFormV2 = ({ data, onSubmit, modalType, header, mode, view }) => {
  const { fields: dataFields, form } = data;

  // State management
  const [fields, setFields] = useState(dataFields);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasDefaultValue, setHasDefaultValue] = useState(mode === 'edit');
  const [modalVisibility, setModalVisibility] = useState({});
  const [modalDataMap, setModalDataMap] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [modalFieldOptionsMap, setModalFieldOptionsMap] = useState({});
  const [editModeInitialized, setEditModeInitialized] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);
  const [tableData, setTableData] = useState({});

  // Refs to prevent unnecessary re-render
  const previousDataRef = useRef();
  const initialModalDataSet = useRef(false);
  const lastVisibilityTriggers = useRef({});

  // Memoized default values including modal data
  const processedDefaultValues = useMemo(() => {
    if (!dataFields) return {};

    const defaults = {};

    Object.values(dataFields).forEach(field => {
      // Handle regular field defaults
      if (field.defaultValue !== undefined && field.defaultValue !== null) {
        defaults[field.id] = field.defaultValue;
      }

      // Handle modal data as form values (KEY FIX)
      if (
        field.type === 'jsonb' &&
        field.modal?.rows &&
        field.modal.rows.length > 0
      ) {
        defaults[field.id] = field.modal.rows;
      }

      // Set appropriate defaults for different field types
      switch (field.type) {
        case 'toggle':
          if (defaults[field.id] === undefined) {
            defaults[field.id] = false;
          }
          break;
        case 'dropdown':
          if (field.multiSelect && defaults[field.id] === undefined) {
            defaults[field.id] = [];
          } else if (!field.multiSelect && defaults[field.id] === undefined) {
            defaults[field.id] = '';
          }
          break;
        case 'checkbox':
          if (defaults[field.id] === undefined) {
            defaults[field.id] = [];
          }
          break;
        case 'jsonb':
          if (defaults[field.id] === undefined) {
            defaults[field.id] = [];
          }
          break;
        default:
          if (defaults[field.id] === undefined) {
            defaults[field.id] = '';
          }
      }
    });

    // console.log(
    //   '🔧 Processed default values (including modal data):',
    //   defaults
    // );
    return defaults;
  }, [dataFields]);

  // Memoized dependency chain
  const dependencyChain = useMemo(() => {
    const chain = {};
    Object.values(fields).forEach(field => {
      if (field.dependents && Array.isArray(field.dependents)) {
        field.dependents.forEach(dependentId => {
          if (!chain[field.id]) {
            chain[field.id] = [];
          }
          chain[field.id].push(dependentId);
        });
      }
    });
    return chain;
  }, [fields]);

  // Memoized active fields function
  const getActiveFields = useCallback(
    (fields, formValues, visibilityMap, getAllDependentFields) => {
      const fieldList = Object.values(fields);

      const getDisabledByField = fieldId => {
        return fieldList.find(
          f => f.disables?.includes(fieldId) && formValues[f.id]
        )?.id;
      };

      const inactiveFields = new Set();
      fieldList.forEach(field => {
        const disabledBy = getDisabledByField(field.id);
        if (disabledBy) {
          inactiveFields.add(field.id);
          getAllDependentFields(field.id).forEach(depId => {
            inactiveFields.add(depId);
          });
        }
      });

      return fieldList.filter(field => {
        if (
          visibilityMap.get(field.id) === false ||
          inactiveFields.has(field.id)
        ) {
          return false;
        }

        if (field.visibilityRule) {
          try {
            const isVisible = new Function(
              'formValues',
              `return ${field.visibilityRule}`
            )(formValues);
            return isVisible;
          } catch (e) {
            console.error(
              `Error evaluating visibility rule for field ${field.id}:`,
              e
            );
            return true;
          }
        }

        return true;
      });
    },
    []
  );

  // Enhanced field visibility logic
  const getFieldVisibility = useCallback((fields, formValues) => {
    const visibilityMap = new Map();
    const fieldList = Object.values(fields);

    // Hide fields based on radio_group selection
    fieldList.forEach(field => {
      if (field.type === 'radio_group' && field.hide) {
        const selectedValue = formValues[field.id] ?? field.defaultValue;
        Object.entries(field.hide).forEach(([radioVal, hideArr]) => {
          hideArr.forEach(hideFieldId => {
            visibilityMap.set(hideFieldId, selectedValue !== radioVal);
          });
        });
      }
    });

    // Find toggle fields with hide options
    const toggleFields = fieldList.filter(
      field =>
        field.type === 'toggle' &&
        Array.isArray(field.hide) &&
        field.hide.length > 0
    );

    toggleFields.forEach(toggleField => {
      const toggleValue =
        formValues[toggleField.id] ?? toggleField.defaultValue ?? false;

      const isToggleOn =
        toggleValue === true || toggleValue === 1 || toggleValue === '1';

      // Handle nested array hide configuration
      if (Array.isArray(toggleField.hide[0])) {
        const hideOnToggleOn = toggleField.hide[0];
        const hideOnToggleOff = toggleField.hide[1] || [];

        hideOnToggleOn.forEach(hiddenFieldId => {
          visibilityMap.set(hiddenFieldId, !isToggleOn);
        });

        hideOnToggleOff.forEach(hiddenFieldId => {
          visibilityMap.set(hiddenFieldId, isToggleOn);
        });
      } else {
        // Handle simple array hide configuration
        toggleField.hide.forEach((hiddenFieldId, index) => {
          visibilityMap.set(
            hiddenFieldId,
            isToggleOn ? index === 0 : index === 1
          );
        });
      }
    });

    return visibilityMap;
  }, []);

  // Get all dependent fields recursively
  const getAllDependentFields = useCallback(
    (fieldId, visited = new Set()) => {
      if (visited.has(fieldId)) return [];
      visited.add(fieldId);

      const dependents = dependencyChain[fieldId] || [];
      const allDependents = [...dependents];

      dependents.forEach(depId => {
        const nestedDependents = getAllDependentFields(depId, visited);
        allDependents.push(...nestedDependents);
      });

      return allDependents;
    },
    [dependencyChain]
  );

  // Form setup with memoized resolver
  const formResolver = useMemo(() => {
    return async (data, context, options) => {
      const formValues = data;
      const activeFields = getActiveFields(
        fields,
        formValues,
        new Map(),
        getAllDependentFields
      );
      const schema = generateValidationSchema({
        fields: activeFields,
        dependencyChain,
        visibilityMap
      });
      return yupResolver(schema)(data, context, options);
    };
  }, [fields, dependencyChain, getActiveFields, getAllDependentFields]);

  const methods = useForm({
    resolver: formResolver,
    mode: 'all',
    defaultValues: processedDefaultValues
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
    setValue,
    reset,
    getValues
  } = methods;

  // Watch only specific fields that affect visibility
  const visibilityTriggerFields = useMemo(() => {
    return Object.values(fields)
      .filter(
        field =>
          (field.type === 'radio_group' && field.hide) ||
          (field.type === 'toggle' && field.hide)
      )
      .map(field => field.id);
  }, [fields]);

  const visibilityTriggerValues = watch(visibilityTriggerFields);
  const formValues = watch();

  // Calculate visibility map only when trigger fields change
  const [visibilityMap, setVisibilityMap] = useState(new Map());

  useEffect(() => {
    const currentTriggers = {};
    visibilityTriggerFields.forEach((fieldId, index) => {
      currentTriggers[fieldId] = visibilityTriggerValues[index];
    });

    // Check if triggers actually changed
    const triggersChanged =
      JSON.stringify(currentTriggers) !==
      JSON.stringify(lastVisibilityTriggers.current);

    if (triggersChanged) {
      lastVisibilityTriggers.current = currentTriggers;
      const newVisibilityMap = getFieldVisibility(fields, formValues);
      setVisibilityMap(newVisibilityMap);
    }
  }, [
    visibilityTriggerValues,
    visibilityTriggerFields,
    fields,
    formValues,
    getFieldVisibility
  ]);

  // Initialize form with default values and modal data
  useEffect(() => {
    if (
      processedDefaultValues &&
      Object.keys(processedDefaultValues).length > 0 &&
      !formInitialized
    ) {
      // console.log(
      //   '📝 Initializing form with default values:',
      //   processedDefaultValues
      // );
      reset(processedDefaultValues);
      setFormInitialized(true);
    }
  }, [processedDefaultValues, reset, formInitialized]);

  // Set up modal data map for edit mode (optimized)
  useEffect(() => {
    if (mode === 'edit' && !initialModalDataSet.current) {
      const modalMap = {};
      Object.values(fields)
        .filter(
          field =>
            field.type === 'jsonb' &&
            field.modal?.rows &&
            field.modal.rows.length > 0
        )
        .forEach(field => {
          modalMap[field.id] = field.modal.rows;
        });

      if (Object.keys(modalMap).length > 0) {
        console.log('🔧 Setting initial modal data map:', modalMap);
        setModalDataMap(modalMap);
        initialModalDataSet.current = true;
      }
    }
  }, [mode, fields]);

  // Initialize selectedRows from defaultValue (optimized)
  useEffect(() => {
    if (selectedRows.length === 0) {
      const formReportField = Object.values(fields).find(
        f => f.type === 'form_report'
      );
      if (
        formReportField?.defaultValue !== undefined &&
        formReportField.defaultValue !== null
      ) {
        const defaultValue = Array.isArray(formReportField.defaultValue)
          ? formReportField.defaultValue
          : [formReportField.defaultValue];
        setSelectedRows(defaultValue);
      }
    }
  }, [fields, selectedRows.length]);

  // Reset edit mode flag when mode changes
  useEffect(() => {
    if (mode !== 'edit') {
      setEditModeInitialized(false);
      initialModalDataSet.current = false;
      setFormInitialized(false);
    }
  }, [mode]);

  // Memoized event handlers
  const handleToggleChange = useCallback(
    (toggleField, newValue) => {
      setValue(toggleField.id, newValue);

      if (toggleField?.hide && Array.isArray(toggleField.hide[0])) {
        const fieldsToReset = newValue
          ? toggleField.hide[0]
          : toggleField.hide[1] || [];
        fieldsToReset.forEach(fieldId => {
          const field = Object.values(fields).find(f => f.id === fieldId);
          if (field) {
            setValue(fieldId, field.defaultValue ?? '');
          }
        });
      }
    },
    [setValue, fields]
  );

  // Special handler for radio group changes
  const handleRadioGroupChange = useCallback(
    (field, value) => {
      setValue(field.id, value);

      // Reset hidden fields based on radio selection
      if (field.hide) {
        Object.entries(field.hide).forEach(([radioVal, hideFieldIds]) => {
          if (radioVal !== value) {
            // Reset fields that should be hidden
            hideFieldIds.forEach(fieldId => {
              const targetField = fields[fieldId];
              if (targetField) {
                const defaultValue =
                  targetField.defaultValue ??
                  (targetField.type === 'dropdown' && targetField.multiSelect
                    ? []
                    : '');
                setValue(fieldId, defaultValue);
              }
            });
          }
        });
      }

      // Only trigger field change if it has dependents
      if (field.dependents && field.dependents.length > 0) {
        handleFieldChange(field.id, value);
      }
    },
    [setValue, fields]
  );

  const handleDateRangeChange = useCallback(
    (field, start, end) => {
      if (!start || !end) return;
      if (new Date(start) > new Date(end)) {
        console.error('Start date cannot be after end date.');
        return;
      }
      const start_date = format(new Date(start), 'yyyy-MM-dd');
      const end_date = format(new Date(end), 'yyyy-MM-dd');
      setValue(field.id, { start_date, end_date });
    },
    [setValue]
  );

  const handleRangeChange = useCallback(
    (field, start, end) => {
      if (!start || !end) return;
      if (new Date(start) > new Date(end)) {
        console.error('Start date cannot be after end date.');
        return;
      }
      const start_date = `yyyy-${format(new Date(start), 'MM-dd')}`;
      const end_date = `yyyy-${format(new Date(end), 'MM-dd')}`;
      setValue(field.id, { start_date, end_date });
    },
    [setValue]
  );

  const handleSelectRow = useCallback(
    (field, rowId) => {
      if (field?.isMulti === false) {
        setSelectedRows([rowId]);
        setValue(field.id, rowId);
      } else {
        setSelectedRows(prev => {
          const newSelected = prev.includes(rowId)
            ? prev.filter(id => id !== rowId)
            : [...prev, rowId];
          setValue(field.id, newSelected);
          return newSelected;
        });
      }
    },
    [setValue]
  );

  const handleRowChange = useCallback((tableName, updatedRows) => {
    setTableData(prevState => ({
      ...prevState,
      [tableName]: updatedRows
    }));
  }, []);

  // Optimized date formatting function
  const formatDatesInObject = useCallback(obj => {
    const formattedObj = {};
    for (const key of Object.keys(obj)) {
      const value = obj[key];
      if (value instanceof Date && isValid(value)) {
        formattedObj[key] = format(value, 'yyyy-MM-dd');
      } else if (typeof value === 'string') {
        const parsedDate = parse(
          value,
          "EEE MMM dd yyyy HH:mm:ss 'GMT'XXX",
          new Date()
        );
        if (isValid(parsedDate)) {
          formattedObj[key] = format(parsedDate, 'yyyy-MM-dd');
        } else {
          formattedObj[key] = value;
        }
      } else {
        formattedObj[key] = value;
      }
    }
    return formattedObj;
  }, []);

  // Optimized submit handler
  const onSubmitHandler = useCallback(
    async formData => {
      if (isProcessing) return;
      setIsProcessing(true);

      try {
        const allFormValues = getValues();
        const formattedData = formatDatesInObject(allFormValues);

        console.log(
          '📤 Submitting all form values (including modal data):',
          formattedData
        );

        const activeFields = getActiveFields(
          fields,
          formattedData,
          visibilityMap,
          getAllDependentFields
        );
        const activeFieldIds = new Set(activeFields.map(f => f.id));

        const filteredFormValues = Object.keys(formattedData).reduce(
          (acc, key) => {
            if (activeFieldIds.has(key)) {
              acc[key] = formattedData[key];
            }
            return acc;
          },
          {}
        );

        // Handle special cases
        if (
          filteredFormValues?.received_items &&
          Array.isArray(filteredFormValues.received_items) &&
          filteredFormValues.received_items.length === 2 &&
          filteredFormValues.received_items[0] ===
            filteredFormValues.received_items[1]
        ) {
          filteredFormValues.received_items =
            filteredFormValues.received_items[0];
        }

        const finalSubmitData = {
          ...filteredFormValues,
          ...modalDataMap
        };

        console.log('🚀 Final submit data:', finalSubmitData);
        await onSubmit(finalSubmitData);
      } catch (error) {
        console.error('Submission error:', error);
      } finally {
        setIsProcessing(false);
      }
    },
    [
      isProcessing,
      getValues,
      formatDatesInObject,
      fields,
      visibilityMap,
      getActiveFields,
      getAllDependentFields,
      modalDataMap,
      onSubmit
    ]
  );

  // Dependency management
  const { buildDependencyChain, fetchDependentData } = useDependencyManager(
    fields,
    formValues,
    setValue,
    useCallback((fieldId, value) => {
      setFields(prev => ({
        ...prev,
        [fieldId]: { ...prev[fieldId], options: value }
      }));
    }, [])
  );

  const handleFieldChange = useCallback(
    async (fieldId, value) => {
      setValue(fieldId, value);

      // Check if this field has any dependents before fetching
      const field = fields[fieldId];
      if (field?.dependents && field.dependents.length > 0) {
        await fetchDependentData(fieldId, value, apiClient);
      }
    },
    [setValue, fetchDependentData, fields]
  );

  // Initialize dependency chain
  useEffect(() => {
    buildDependencyChain();
  }, [buildDependencyChain]);

  // Edit mode dependency initialization (optimized)
  useEffect(() => {
    if (
      mode === 'edit' &&
      formInitialized &&
      !editModeInitialized &&
      Object.keys(formValues).length > 0
    ) {
      console.log('🚀 Initializing edit mode dependencies...');

      const timeoutId = setTimeout(async () => {
        const fieldsWithDependencies = Object.values(fields).filter(
          field =>
            field.dependents &&
            field.dependents.length > 0 &&
            formValues[field.id] !== undefined &&
            formValues[field.id] !== null &&
            formValues[field.id] !== ''
        );

        console.log(
          '🔍 Fields with dependencies found:',
          fieldsWithDependencies.map(f => ({
            id: f.id,
            value: formValues[f.id]
          }))
        );

        for (const field of fieldsWithDependencies) {
          const fieldValue = formValues[field.id];
          try {
            await fetchDependentData(field.id, fieldValue, apiClient);
          } catch (error) {
            console.error(
              `❌ Error fetching dependencies for ${field.id}:`,
              error
            );
          }
        }

        setEditModeInitialized(true);
        console.log('✅ Edit mode dependencies initialization completed');
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [
    mode,
    formInitialized,
    editModeInitialized,
    formValues,
    fields,
    fetchDependentData
  ]);

  // Modal event listeners (optimized)
  useEffect(() => {
    const handleModalOptionsUpdate = event => {
      const { modalId, fieldId, options } = event.detail;
      setModalFieldOptionsMap(prev => ({
        ...prev,
        [`${modalId}.${fieldId}`]: options
      }));
    };

    const handleModalFieldReset = event => {
      const { modalId, fieldId, value } = event.detail;
      window.dispatchEvent(
        new CustomEvent(`resetModalField_${modalId}`, {
          detail: { fieldId, value }
        })
      );
    };

    window.addEventListener(
      'updateModalFieldOptions',
      handleModalOptionsUpdate
    );
    window.addEventListener('resetModalField', handleModalFieldReset);

    return () => {
      window.removeEventListener(
        'updateModalFieldOptions',
        handleModalOptionsUpdate
      );
      window.removeEventListener('resetModalField', handleModalFieldReset);
    };
  }, []);

  // Enhanced modal submit handler
  const handleModalSubmit = useCallback(
    ({ data, modalId }) => {
      console.log(`✅ Modal ${modalId} submitted with data:`, data);

      setModalDataMap(prev => ({
        ...prev,
        [modalId]: data
      }));

      setValue(modalId, data);
    },
    [setValue]
  );

  const toggleModal = useCallback((modalId, isVisible) => {
    setModalVisibility(prev => ({
      ...prev,
      [modalId]: isVisible
    }));
  }, []);

  // Create a callback for onFormMetadataChange OUTSIDE of useMemo
  const onFormMetadataChangeCallback = useCallback((fieldId, value) => {
    setFields(prev => ({
      ...prev,
      [fieldId]: { ...prev[fieldId], options: value }
    }));
  }, []);

  // Get visible fields based on visibility map
  const visibleFields = useMemo(() => {
    const fieldList = Object.values(fields);
    return fieldList.filter(field => {
      // Check visibility map
      if (
        visibilityMap.has(field.id) &&
        visibilityMap.get(field.id) === false
      ) {
        return false;
      }

      // Check visibility rule
      if (field.visibilityRule) {
        try {
          const isVisible = new Function(
            'formValues',
            `return ${field.visibilityRule}`
          )(formValues);
          return isVisible;
        } catch (e) {
          console.error(
            `Error evaluating visibility rule for field ${field.id}:`,
            e
          );
          return true;
        }
      }

      return true;
    });
  }, [fields, visibilityMap, formValues]);

  // Render fields
  const renderField = field => {
    const currentField = fields[field.id];

    if (currentField.type === 'jsonb') {
      return (
        <Col key={currentField.id} lg={12}>
          <div className="text-end mt-3">
            <Button
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                toggleModal(currentField.id, true);
              }}
              className="mb-3"
            >
              Add New
            </Button>
          </div>
          <ModalForm
            modalConfig={{
              ...currentField.modal,
              id: currentField.id
            }}
            onClose={visible => toggleModal(currentField.id, visible)}
            show={!!modalVisibility[currentField.id]}
            onSubmit={handleModalSubmit}
            initialData={currentField.modal?.rows || []}
            modalFieldOptions={modalFieldOptionsMap}
            parentFormValues={formValues}
          />
        </Col>
      );
    }

    return (
      <Col
        lg={
          modalType
            ? currentField.type === 'textarea' ||
              currentField.type === 'radio_group'
              ? 12
              : 6
            : currentField.type === 'textarea' ||
              currentField.type === 'radio_group' ||
              currentField.type === 'sectionHeader' ||
              currentField.type === 'form_report'
            ? 12
            : 4
        }
        key={currentField.id}
        className="g-lg-4 g-2"
      >
        <DynamicNormalForm
          fields={fields}
          metadata={currentField}
          control={control}
          mode={mode}
          register={methods.register}
          errors={errors}
          onToggleChange={
            currentField.type === 'toggle'
              ? value => handleToggleChange(currentField, value)
              : undefined
          }
          onRadioGroupChange={
            currentField.type === 'radio_group'
              ? value => handleRadioGroupChange(currentField, value)
              : undefined
          }
          hasDefaultValue={hasDefaultValue}
          setHasDefaultValue={setHasDefaultValue}
          handleDateRangeChange={(start, end) =>
            handleDateRangeChange(currentField, start, end)
          }
          handleMonthDayChange={(start, end) =>
            handleRangeChange(currentField, start, end)
          }
          modalDataMap={modalDataMap}
          hideFields={() => {}}
          onFormMetadataChange={onFormMetadataChangeCallback}
          handleSelectRow={handleSelectRow}
          selectedRows={selectedRows}
          onFieldChange={handleFieldChange}
        />
      </Col>
    );
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmitHandler, errors => {
          console.log('Validation errors:', errors);
        })}
        noValidate
      >
        <Row className="gx-2 gy-3">
          {visibleFields.map(field => renderField(field))}
        </Row>

        {Object.values(fields)
          .filter(field => field.table)
          .map(field => (
            <DynamicTableForm
              key={field.id}
              control={methods.control}
              errors={errors}
              tableConfig={field}
              onRowChange={handleRowChange}
            />
          ))}

        <div className="text-end mt-3">
          {!view && (
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={isSubmitting || isProcessing}
              className="w-100 w-sm-auto"
            >
              {isProcessing || isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          )}
        </div>
      </form>
    </FormProvider>
  );
};

export default React.memo(DynamicFormV2);
